// MOVE TO BIN (Karan 30/10/2026, from the "7241 · 5. Drawings" folder: "need a
// delete option please").
//
// LAWS (do not undo):
//  1. Track never deletes a Drive file. "Move to bin" is files.update with
//     trashed:true, which Drive keeps for 30 days and anyone with access can
//     restore. There is no permanent delete anywhere in Track, and this module
//     never calls files.delete or emptyTrash.
//  2. It writes with the PERSON's Google token, the way Rename does. If Drive
//     refuses (a Contributor on a shared drive cannot bin), Drive's own reason
//     is shown and nothing else is tried: no service account, no fallback.
//  3. Never offered on a project folder, on the numbered template folders
//     directly inside one (1. Contract … 7. Images), or on a file someone has
//     marked in use (fileUse, fresh). A shortcut is binned as the SHORTCUT —
//     what it points to is never touched, and a folder's count never follows
//     a shortcut inside it.
//  4. Drive first, register second: if Drive refuses, the register is not
//     touched. A register row whose file is binned is set to status 'binned'
//     (register-status.js), and Undo puts back exactly the status it had.
//
// Everything here is pure or takes its Drive / Firestore calls as arguments, so
// the guard, the wording and the write order are unit-tested
// (test/files-bin.test.mjs) without a browser.
import { folderCode, walkFolder } from './drive-folders.js';
import { BINNED } from './register-status.js';

const FOLDER = 'application/vnd.google-apps.folder';
const isFolder = (f) => !!f && f.mimeType === FOLDER;
const firstName = (s) => String(s || '').trim().split(/\s+/)[0] || '';

/** "8593 Harness job…", "A0352 …": a folder named like a project's own folder. */
export function looksLikeProjectFolder(name) {
  return /^(A\d{4}|\d{4})(?=\s|$|[-–—·_])(?!-\d)/.test(String(name || '').trim());
}

/** The Drive id the bin acts on: a shortcut's OWN id, never its target's. */
export const binId = (f) => (f && (f.shortcutId || f.id)) || '';
/** The key a row is selected by (unique even when a shortcut and its target share a folder). */
export const rowKey = binId;

/**
 * Can this row be moved to the bin? → { ok:true } | { ok:false, why, tag }
 *   why  a sentence for a toast; tag  a short label for the greyed menu item.
 * ctx: { parentName, inUse(f) → { by } | null }
 */
export function binGuard(f, { parentName = '', inUse = () => null } = {}) {
  if (!f || !binId(f)) return { ok: false, why: 'Nothing to move.', tag: '' };
  if (f.shortcutId) return { ok: true };   // the shortcut only (law 3)
  if (isFolder(f)) {
    if (looksLikeProjectFolder(f.name)) return { ok: false, tag: 'Project folder', why: `${f.name} is a project folder. Track never bins one.` };
    if (folderCode(f.name) && looksLikeProjectFolder(parentName)) {
      return { ok: false, tag: 'Template folder', why: `${f.name} is one of the project’s template folders. Track never bins those.` };
    }
    return { ok: true };
  }
  const u = inUse(f);
  if (u) {
    const who = firstName(u.by) || 'someone';
    return { ok: false, tag: `In use · ${who}`, why: `${f.name} is marked in use by ${who}. Try again once they have closed it.` };
  }
  return { ok: true };
}

const plural = (n, one, many = `${one}s`) => `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`;
/** "(12 files)", "(1 file)", "(no files)", "(more than 400 files)". */
export function filesInText(n, truncated = false) {
  if (truncated) return `(more than ${plural(n, 'file')})`;
  return n ? `(${plural(n, 'file')})` : '(no files)';
}

/**
 * The confirm's words.
 * items: [{ name, mimeType, shortcutId?, count?, truncated? }] — count/truncated
 * are a folder's files below it (from countFolder).
 * → { title, restore, contents: string|null, confirmLabel }
 */
export function binCopy(items = []) {
  const list = items.filter(Boolean);
  const one = list.length === 1 ? list[0] : null;
  const folders = list.filter((f) => isFolder(f) && !f.shortcutId);
  const inFolders = folders.reduce((n, f) => n + (Number(f.count) || 0), 0);
  const cut = folders.some((f) => f.truncated);
  if (one) {
    if (one.shortcutId) {
      return { title: `Move the shortcut ${one.name} to the bin?`, restore: 'What it points to stays where it is. You can restore the shortcut from Drive’s bin for 30 days.', contents: null, confirmLabel: 'Move to bin' };
    }
    if (isFolder(one)) {
      return { title: `Move ${one.name} and everything in it ${filesInText(Number(one.count) || 0, !!one.truncated)} to the bin?`, restore: 'You can restore it from Drive’s bin for 30 days.', contents: null, confirmLabel: 'Move to bin' };
    }
    return { title: `Move ${one.name} to the bin?`, restore: 'You can restore it from Drive’s bin for 30 days.', contents: null, confirmLabel: 'Move to bin' };
  }
  const n = list.length;
  const contents = folders.length
    ? `${plural(folders.length, 'folder')} and everything in ${folders.length === 1 ? 'it' : 'them'} ${filesInText(inFolders, cut)}`
    : null;
  return { title: `Move ${plural(n, 'item')} to the bin?`, restore: 'You can restore them from Drive’s bin for 30 days.', contents, confirmLabel: `Move ${n} to bin` };
}

/**
 * Register rows that stop a move (read before the confirm opens):
 *   - an issued report or issue sheet is the Reports module's (its rows are
 *     server-written; the rules refuse a browser update), so Track leaves it;
 *   - any other register row can only be updated by a reviewer (Owner, Director,
 *     Finance, Coordinator — firestore.rules documents/update). Binning the file
 *     without updating its row would leave the register saying Current, so for
 *     anyone else it is not offered (fail conservative).
 * → null | { why, tag }
 */
export function registerBlock(name, rows = [], { reviewer = false } = {}) {
  const live = (rows || []).filter(Boolean);
  if (!live.length) return null;
  if (live.some((r) => ['report', 'issue-sheet'].includes(String(r.type || '')))) {
    return { tag: 'Issued report', why: `${name} is an issued report. Reports keeps its register, so Track leaves it where it is.` };
  }
  if (!reviewer) {
    return { tag: 'Reviewer', why: `${name} is in the document register. Only an Owner, Director, Finance or Coordinator can bin it, so the register stays right.` };
  }
  return null;
}

/** The toast after a move: "Moved to bin" / "Moved 3 to bin". */
export const binToastText = (n) => (n === 1 ? 'Moved to bin' : `Moved ${n} to bin`);

/**
 * Every file below a folder, for the confirm's count and the register. Shortcuts
 * inside are not followed (binning the folder bins the shortcut, not its target)
 * and are not counted as files. list(id) → children (listFolder).
 * → { files:[{ id, name }], count, truncated, errors }
 */
export async function countFolder(list, folderId, { maxDepth = 6, maxFolders = 120 } = {}) {
  const own = (id) => list(id).then((kids) => (kids || []).filter((c) => !c.shortcutId));
  const { files, errors, truncated } = await walkFolder(own, folderId, { maxDepth, maxFolders });
  return { files, count: files.length, truncated: truncated || errors.length > 0, errors };
}

/**
 * Move the items to the bin. Drive first, register second (law 4).
 *
 * deps:
 *   trash(id)       → Promise  files.update trashed:true with the person's token
 *   untrash(id)     → Promise  trashed:false (Undo)
 *   register(item)  → [{ id, status }] register rows for the item and, for a
 *                     folder, every file below it (looked up BEFORE the move)
 *   setStatus(rowId, status) → Promise
 * items: [{ id (binId), name, … }]
 *
 * → { moved:[item], failed:[{ item, reason }], registerFailed:[{ name, reason }],
 *     rows:[{ id, was }], undo() }
 * undo() → { restored:[item], failed:[{ item, reason }], registerFailed:[…] }
 */
export async function moveToBin(items, deps) {
  const { trash, untrash, register = async () => [], setStatus = async () => {} } = deps || {};
  const moved = [], failed = [], registerFailed = [], rows = [];
  const rowsOf = new Map();
  for (const it of items) {
    // The register rows are read before the move, so the status they had is known.
    let regs = [];
    try { regs = (await register(it)) || []; } catch (e) { registerFailed.push({ name: it.name, reason: reasonOf(e) }); }
    try {
      await trash(it.id);
    } catch (e) {
      failed.push({ item: it, reason: reasonOf(e) });
      continue;   // Drive refused: the register is not touched
    }
    moved.push(it);
    const mine = [];
    for (const r of regs) {
      if (!r || !r.id || String(r.status || '').toLowerCase() === BINNED) continue;
      try {
        await setStatus(r.id, BINNED);
        const row = { id: r.id, was: r.status || 'current', item: it.id };
        rows.push(row); mine.push(row);
      } catch (e) { registerFailed.push({ name: it.name, reason: reasonOf(e) }); }
    }
    rowsOf.set(it.id, mine);
  }
  const undo = async () => {
    const restored = [], uFailed = [], uReg = [];
    for (const it of moved) {
      try { await untrash(it.id); } catch (e) { uFailed.push({ item: it, reason: reasonOf(e) }); continue; }
      restored.push(it);
      for (const row of rowsOf.get(it.id) || []) {
        try { await setStatus(row.id, row.was); } catch (e) { uReg.push({ name: it.name, reason: reasonOf(e) }); }
      }
    }
    return { restored, failed: uFailed, registerFailed: uReg };
  };
  return { moved, failed, registerFailed, rows, undo };
}

/** Drive's own words for a refusal, plainly (gFetchWith puts error.message on the Error). */
export function reasonOf(e) {
  const m = String((e && e.message) || e || '').trim();
  return m || 'Drive gave no reason';
}

/** One line for a refusal toast: "Drive didn’t move 8593-05-0101.pdf: The user does not have sufficient permissions for this file." */
export function refusalText(failed = [], verb = 'move') {
  if (!failed.length) return '';
  const f = failed[0];
  const more = failed.length > 1 ? ` (and ${plural(failed.length - 1, 'other')})` : '';
  return `Drive didn’t ${verb} ${f.item.name}${more}: ${f.reason}`;
}
